import { Star } from "lucide-react";
import { T } from "../lib/theme";
import { AVG_MONTH_DAYS, daysSince, elapsed, fmtDate } from "../lib/dates";
import type { MemberSummary } from "../lib/types";
import { YouthTag } from "./ui";

/** Row height, condensed from ~78px to shorten a 350-name scroll by about a
 *  third. Exported because VirtualList needs it, and the Members screen reuses
 *  the same metric. */
export const ROW_H = 56;

/** How recently someone prayed still counts as worth mentioning on their row.
 *  Deliberately fixed rather than tied to the configurable prayer warning — this
 *  is passing context, not the warning itself, which lives at point of entry. */
const PRAYER_HINT_DAYS = 90;

/** The signature element: a rail that drains as time passes.
 *
 *  Full means recently asked; an empty dashed outline means due. An earlier
 *  version grew with neglect and was rejected — it read like an achievement
 *  rather than a countdown.
 *
 *  `targetMonths` only scales the gauge. It deliberately does not affect sort
 *  order, and the Settings copy says so out loud.
 */
function Countdown({ lastSpoke, targetMonths }: { lastSpoke: string | null; targetMonths: number }) {
  const target = targetMonths * AVG_MONTH_DAYS;
  // Never-spoken is treated as fully elapsed, which renders as due.
  const used = lastSpoke ? daysSince(lastSpoke) : target;
  const remaining = Math.max(0, Math.min(1, 1 - used / target));
  const due = remaining <= 0;

  return (
    <div
      title={due ? "Due to speak" : `${Math.round(remaining * targetMonths)} mo left`}
      style={{
        width: 3,
        alignSelf: "stretch",
        borderRadius: 2,
        flexShrink: 0,
        background: due ? "transparent" : T.line,
        border: due ? `1px dashed ${T.brass}77` : "none",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        boxSizing: "border-box",
        margin: "8px 0",
      }}
    >
      {!due && (
        <div
          style={{
            height: `${remaining * 100}%`,
            width: "100%",
            borderRadius: 2,
            background: remaining < 0.25 ? T.brass : T.accent,
            opacity: remaining < 0.25 ? 0.9 : 0.55,
          }}
        />
      )}
    </div>
  );
}

export function PersonRow({
  m,
  targetMonths,
  starred,
  onOpen,
  onStar,
}: {
  m: MemberSummary;
  targetMonths: number;
  starred: boolean;
  onOpen: () => void;
  onStar: () => void;
}) {
  const never = !m.last_spoke_date;
  const name = `${m.last_name}, ${m.first_name}`;

  return (
    <div
      className="row-press"
      style={{
        display: "flex",
        alignItems: "stretch",
        height: ROW_H,
        borderBottom: `1px solid ${T.line}`,
        paddingLeft: 14,
        gap: 10,
      }}
    >
      <Countdown lastSpoke={m.last_spoke_date} targetMonths={targetMonths} />

      <button
        onClick={onOpen}
        style={{
          flex: 1,
          textAlign: "left",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 1,
          padding: 0,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
          <span
            className="serif"
            style={{
              fontSize: 16.5,
              fontWeight: 500,
              letterSpacing: "-.005em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
            {m.speaker_type === "youth" && <YouthTag />}
          </span>

          {/* Left blank when never-spoken, so scanning this column the blanks
              *are* the people who have never spoken. "Never spoken" appears
              once per row, in the lower slot. */}
          {!never && m.last_spoke_date && (
            <span className="tnum" style={{ fontSize: 12.5, fontWeight: 500, whiteSpace: "nowrap" }}>
              {fmtDate(m.last_spoke_date)}
            </span>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
          <span
            style={{
              fontSize: 11,
              color: T.faint,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {m.speak_count === 0 ? "no talks" : `${m.speak_count} talk${m.speak_count > 1 ? "s" : ""}`}
            {m.last_prayer_date && daysSince(m.last_prayer_date) < PRAYER_HINT_DAYS && (
              <span style={{ color: T.accent }}> · prayed {elapsed(m.last_prayer_date)}</span>
            )}
          </span>

          <span
            style={{
              fontSize: 11,
              color: never ? T.brass : T.muted,
              fontWeight: never ? 600 : 400,
              whiteSpace: "nowrap",
            }}
          >
            {never ? "never spoken" : elapsed(m.last_spoke_date)}
          </span>
        </div>
      </button>

      <button
        onClick={onStar}
        aria-label={starred ? `Stop considering ${name}` : `Consider ${name}`}
        aria-pressed={starred}
        style={{
          padding: "0 14px 0 6px",
          color: starred ? T.brass : "#D2DAE4",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Star size={18} fill={starred ? T.brass : "none"} strokeWidth={2} />
      </button>
    </div>
  );
}
